// Self-serve sign-up. Creates a Supabase user; the new account lands on
// the Pending page until a god/admin approves them via /admin/users.
//
// If Supabase has "Confirm email" enabled (Authentication → Settings),
// the user receives a verification email and can't sign in until they
// click the link. Otherwise they're signed in immediately.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";

export function SignUpPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<"signed-in" | "needs-confirm" | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { data, error: signupError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: { signup_message: message || null },
        },
      });
      if (signupError) {
        setError(signupError.message);
        return;
      }

      // If Supabase is configured to require email confirmation, session
      // is null until the user clicks the link. Otherwise it's set.
      if (data.session) {
        setSuccess("signed-in");
        setTimeout(() => navigate("/"), 800);
      } else {
        setSuccess("needs-confirm");
      }
    } finally {
      setBusy(false);
    }
  }

  if (success === "needs-confirm") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-8 text-center shadow-sm">
          <div className="text-3xl" aria-hidden>📬</div>
          <h1 className="text-xl font-semibold">Check your email</h1>
          <p className="text-sm text-muted-foreground">
            We sent a confirmation link to <strong>{email}</strong>. Click
            it to verify your account, then come back and sign in.
          </p>
          <Button asChild variant="outline" className="w-full">
            <Link to="/">Back to sign in</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6 rounded-lg border bg-card p-8 shadow-sm">
        <div className="space-y-2 text-center">
          <div className="text-3xl" aria-hidden>
            🧌
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Request access
          </h1>
          <p className="text-sm text-muted-foreground">
            Approval required after sign-up.{" "}
            <Badge variant="accent">invite-only</Badge>
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
            <p className="text-xs text-muted-foreground">
              At least 8 characters.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="msg">Message to admin (optional)</Label>
            <textarea
              id="msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi, I'm…"
              rows={3}
              maxLength={500}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating
                account…
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" /> Create account
              </>
            )}
          </Button>
        </form>

        <p className="text-center text-xs text-muted-foreground">
          Already have an account?{" "}
          <Link to="/" className="text-info underline-offset-4 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
